<template>
  <div class="comment-main" v-show="showBox" ref="reply">
    <div class="comment-item">
      <div class="box">
        <div class="avatar-wrapper">
          <el-popover
            placement="bottom-start"
            width="280"
            trigger="click"
            v-model="showNicknameSelector"
            popper-class="nickname-popover"
            @show="$emit('identityPickerShow')"
          >
            <!--
              列表和选中判定完全由父组件（comment/index.vue）算好后传下来，
              这里只负责渲染和把点击抛回去 —— 学校选择的状态只有一份，
              主评论框和楼内回复共用，不能各存一套。
            -->
            <div class="nickname-list">
              <div 
                v-for="nick in identityOptions" 
                :key="nick.key"
                class="nickname-item hand-style"
                :class="{ 'active': currentIdentityKey === nick.key }"
                @click="selectNickname(nick)"
              >
                <smart-img mode="avatar" :src="nick.avatar" :alt="nick.name" class="nickname-avatar" />
                <div class="nickname-info">
                  <div class="nickname-name">{{ nick.name }}</div>
                  <div class="nickname-type" :class="nick.nickType === 1 ? 'is-anonymous' : 'is-real'">{{ identityTypeText(nick) }}</div>
                </div>
                <i v-if="currentIdentityKey === nick.key" class="el-icon-check"></i>
              </div>
            </div>
            <div class="avatar hand-style" slot="reference">
              <!--
                同 comment/index.vue：原来第三个分支的游客兜底图指向已失效的
                img.shiyit.com，实际是裂图。合成一个 <smart-img>，取不到就用内联 SVG。
              -->
              <smart-img
                mode="avatar"
                :src="(selectedNickname && selectedNickname.avatar) || (user && user.avatar) || ''"
                alt="我的头像"
                eager
              />
              <div class="avatar-badge">
                <i class="el-icon-caret-bottom"></i>
              </div>
            </div>
          </el-popover>
        </div>
        <div class="ml-3">
          <div data-v-0089e256="" class="comment-input">
            <!-- 输入框里的 @昵称 染蓝的「镜像层」方案，取舍和注意事项见
                 主评论框 components/comment/index.vue 里同一处的注释 -->
            <div class="comment-textarea-wrap">
              <!-- 片段写在同一行、标签之间不留空白：镜像是 pre-wrap，
                   换行和缩进会变成可见空格，镜像就比真实文字宽出一截 -->
              <div v-if="showInputHighlight" ref="inputMirror" class="comment-input-mirror" aria-hidden="true"><template v-for="(seg, segIndex) in inputSegments"><span v-if="seg.type === 'at'" :key="`mirror-at-${segIndex}`" class="mirror-at-text">{{ seg.text }}</span><span v-else :key="`mirror-txt-${segIndex}`">{{ seg.text }}</span></template></div>
              <el-input
                ref="textareaRef"
                type="textarea"
                v-model="commentContent"
                placeholder="说点什么呢"
                :rows="4"
                :maxlength="500"
                show-word-limit
                :class="['comment-textarea', { 'is-highlighting': showInputHighlight }]"
                @input="onContentInput"
                @keydown.native="onTextareaKeydown"
              ></el-input>

              <!-- 光标探针：量正文里那个 "@" 的像素位置用，和 textarea 共用同一套盒模型。
                   原理和注意事项见主评论框（components/comment/index.vue）同一处的注释。
                   必须写成一行、标签之间不留空白：这一层是 pre-wrap，空白会参与排版 -->
              <div v-if="atTriggerIndex >= 0" class="comment-caret-probe" aria-hidden="true">{{ commentContent.slice(0, atTriggerIndex) }}<span ref="caretProbeMark" class="comment-caret-mark"></span></div>

              <!-- 选人面板的定位锚点：0×0 空元素，由 updateMentionAnchor 挪到
                   "@" 所在那一行的下沿，面板因此紧跟在 "@" 后面展开 -->
              <MentionPicker
                ref="mentionPicker"
                class="mention-anchor"
                :style="mentionAnchorStyle"
                :selected-ids="selectedAtUserIds"
                @select="handleSelectAtUser"
              >
                <span></span>
              </MentionPicker>
            </div>
          </div>
          <!-- 已选配图的缩略图。选的时候只压缩不上传，点「发布」才传 COS，
               理由见 mixins/commentImageMixin.js -->
          <div v-if="commentSticker" class="comment-sticker-preview">
            <img :src="commentSticker.url" alt="表情" />
            <div class="comment-upload-remove" @click="commentSticker = null">
              <i class="el-icon-close"></i>
            </div>
          </div>
          <div v-if="commentImageList.length" class="comment-upload-list">
            <div class="comment-upload-item" v-for="(url, index) in commentImageList" :key="index">
              <!-- 本地 blob 预览地址，不走 CDN 缩略图（那是给远程图用的） -->
              <img :src="url" alt="回复配图" />
              <div class="comment-upload-remove" @click="removeCommentImage(index)">
                <i class="el-icon-close"></i>
              </div>
            </div>
          </div>

          <div class="comment-btn">
            <Emoji @chooseEmoji="handleChooseEmoji">
              <span class="emoji-btn hand-style">
                <emoji-icon />
              </span>
            </Emoji>
            <StickerPicker
              ref="stickerPicker"
              :mine="stickerMine"
              :packs="stickerPacks"
              :active-pack="stickerToolbarPack"
              @open="onStickerPickerOpen"
              @select="selectCommentSticker"
              @upload="uploadCommentSticker"
              @remove="removeCommentSticker"
              @pin="pinCommentSticker"
              @add-recommend="addRecommendSticker"
            >
              <span class="emoji-btn sticker-entry-btn hand-style" title="斗图">
                <sticker-icon />
              </span>
            </StickerPicker>
            <span
              v-for="pack in stickerPacks"
              :key="pack.id"
              class="emoji-btn sticker-pack-btn hand-style"
              :title="pack.name"
              @click.stop="openStickerPackFromToolbar(pack.id, $event)"
            >
              <img :src="pack.cover" alt="" />
            </span>
            <!-- @ 提及。按钮只负责往光标处插一个 "@"，面板由 onContentInput 顶出来，
                 和用户手打 "@" 走同一条路径，见主评论框 onMentionButtonClick 的注释 -->
            <span
              class="mention-btn hand-style"
              title="提及好友"
              @click="onMentionButtonClick"
            >@</span>
            <!-- 配图。选满就不再渲染上传入口，缩略图那一排本身已经说明了状态 -->
            <el-upload
              v-if="commentImageList.length < commentImageMaxCount"
              class="comment-image-upload"
              :show-file-list="false"
              action="#"
              :http-request="handleCommentImageUpload"
              :before-upload="beforeCommentImageUpload"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
            >
              <span class="image-btn hand-style" :title="`添加图片（最多${commentImageMaxCount}张）`">
                <i class="el-icon-picture-outline"></i>
              </span>
            </el-upload>
            <div style="margin-left: auto">
              <el-button
                  type="info"
                  @click="handleCancle"
                  class="cancle-btn v-comment-btn hand-style"
              >
                取消
              </el-button>
              <el-button
                  @click="addComment"
                  :loading="submitting"
                  :class="['upload-btn', 'v-comment-btn', 'hand-style', { 'has-content': hasContent }]"
              >
                发布
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { userFetch } from '@/api'
import { browserMatch } from '@/utils'
import Emoji from '@/components/emoji'
import EmojiIcon from '@/components/EmojiIcon'
import StickerIcon from '@/components/StickerIcon'
import StickerPicker from '@/components/sticker'
import emojiMixin from '@/mixins/emojiMixin'
import commentImageMixin from '@/mixins/commentImageMixin'
import stickerMixin from '@/mixins/stickerMixin'
import { uploadImageToCos } from '@/utils/cosUpload'
import { isAuditPublished, getAuditBadge } from '@/constant'
import MentionPicker from './MentionPicker.vue'
import {
  MAX_AT_USER_COUNT,
  parseMentionSegments,
  detectMentionQuery,
  insertMention,
  insertMentionAt,
  findMentionTokenAt,
  syncAtUsers,
  buildAtUserIds
} from '@/utils/mention'

// 与 el-input 上的 :maxlength 保持一致
const COMMENT_MAX_LENGTH = 500

export default {
  components: {
    Emoji,
    EmojiIcon,
    StickerIcon,
    MentionPicker,
    StickerPicker
  },
  // commentImageMixin：回复框的选图 / 预览 / 上传，与主评论框共用一份
  mixins: [emojiMixin, commentImageMixin, stickerMixin],
  props: {
    articleInfo: {
      type: Object,
      default: () => ({})
    },
    // 身份 × 学校 的叉乘选项，由父组件算好传下来（见 comment/index.vue 的 identityOptions）
    identityOptions: {
      type: Array,
      default: () => []
    },
    // 当前选中项的标识，拼法见父组件的 currentIdentityKey
    currentIdentityKey: {
      type: String,
      default: ''
    },
    selectedNickname: {
      type: Object,
      default: null
    },
    // 这条回复要挂在哪所学校名下，落到 b_comment.author_school_id 快照
    commentSchoolId: {
      type: String,
      default: ''
    },
    // 不按学校展开时实名身份显示的学校名（父组件已经处理了「用资料学校兜底」）
    commentSchoolName: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      userId: null,
      nickname: null,
      parentId: null,
      index: null,
      commentContent: "",
      replyUserId: null,
      showBox: false,
      level: null,
      showNicknameSelector: false,
      // 发布中标记：防止连点重复提交
      submitting: false,
      commentSticker: null,
      // 已选的被 @ 用户 [{ userId, nickname }]，提交前会用正文再校准一遍
      selectedAtUsers: [],
      // 用户打 "@" 唤起面板时那个 '@' 在正文里的下标，-1 表示没有正在输入的 @。
      // 同时也是光标探针的开关和量测起点（见 updateMentionAnchor）
      atTriggerIndex: -1,
      // 选人面板锚点的内联定位，由 updateMentionAnchor 按 "@" 的像素位置算出来
      mentionAnchorStyle: null,
    }
  },
  computed: {
    // 传给 MentionPicker 用于打勾和挡重复选择
    selectedAtUserIds () {
      return this.selectedAtUsers.map(item => item.userId)
    },

    // 输入框正文切成片段给 @ 高亮镜像层染色，理由见主评论框的同名 computed
    inputSegments () {
      return parseMentionSegments(this.commentContent, this.selectedAtUsers)
    },

    // 正文里真的有 @ 提及时才启用镜像，没有 @ 就让 textarea 保持原样
    showInputHighlight () {
      return this.inputSegments.some(seg => seg.type === 'at')
    },

    // 「发布」按钮的高亮态。只发图不发字也算有内容，
    // 否则用户选好图之后按钮还是灰的，看起来像发不出去
    hasContent () {
      return this.commentContent.trim().length > 0 || this.hasCommentImages || !!this.commentSticker
    },
    // 与主评论框一致：直接读 store，避免登录后仍拿到初始的空对象
    user () {
      const info = this.$store.state.userStore.userInfo
      return info && info.id ? info : null
    }
  },
  watch: {
    // 正文一变就把 @ 高亮镜像滚回和 textarea 一致的位置，理由见主评论框同名 watch
    commentContent () {
      this.$nextTick(this.syncMirrorScroll)
    }
  },
  mounted () {
    // 回复框是 v-show 控制显隐（不是 v-if），组件一开始就挂好了，
    // 这里能直接拿到内部的 textarea
    this.$nextTick(this.bindMirrorScroll)
  },
  beforeDestroy () {
    this.unbindMirrorScroll()
  },
  // 说明：这里原本在 mounted 里给 document 挂了一个匿名的 click 监听，用来把
  // chooseEmoji 置回 false，但 chooseEmoji 并没有参与任何渲染（表情面板的开合由
  // Emoji 组件自己管），而且监听既没有在 beforeDestroy 里移除、又是每条评论一个实例
  // （一篇几十条评论的帖子就会挂几十个 document 监听并在弹窗关闭后继续泄漏）。
  // 该监听已整体移除。
  methods: {
    // 选择昵称 - 通知父组件
    // 身份下拉每一项的副标题。口径与 comment/index.vue 的同名方法一致，
    // 改一处记得同步改另一处
    identityTypeText (option) {
      if (!option) return '实名'
      const prefix = option.nickType === 1 ? '匿名' : '实名'
      if (option.hideSchool) return `${prefix} · 不显示学校`
      const school = option.schoolName || this.commentSchoolName
      return school ? `${prefix} · ${school}` : prefix
    },

    selectNickname (nickname) {
      this.$emit('selectNickname', nickname)
      this.showNicknameSelector = false
    },

    // 处理表情选择
    selectCommentSticker(item) {
      if (!item || !item.url) return
      this.commentSticker = { url: item.url, name: item.name || '' }
      this.resetCommentImages()
      if (!item.added) {
        // 同 components/comment/index.vue：满 100 之后别再重复打请求
        this.collectStickerAfterSend(item)
      }
    },
    async uploadCommentSticker(file) {
      try {
        const url = await uploadImageToCos(file, { directory: 'sticker' })
        await this.addStickerByUrl(url)
        this.$toast.success('已添加')
      } catch (e) {
        this.$toast.error((e && e.message) || '添加失败')
      }
    },
    async pinCommentSticker(item) {
      try {
        await this.pinStickerById(item.id)
      } catch (e) {
        this.$toast.error((e && e.message) || '操作失败')
      }
    },
    async removeCommentSticker(item) {
      try {
        await this.removeStickerById(item.id, item.url)
        this.$toast.success('已删除')
      } catch (e) {
        this.$toast.error((e && e.message) || '删除失败')
      }
    },
    async addRecommendSticker(item) {
      try {
        await this.addStickerByUrl(item.url, item.name)
        this.$toast.success('已添加')
      } catch (e) {
        this.$toast.error((e && e.message) || '添加失败')
      }
    },
    handleChooseEmoji(emoji) {
      const textarea = this.$refs.textareaRef
      if (!textarea) return
      
      // 获取 textarea 的原生 DOM 元素
      const textareaEl = textarea.$el ? textarea.$el.querySelector('textarea') : textarea
      
      // 获取当前光标位置
      const startPos = textareaEl.selectionStart
      const endPos = textareaEl.selectionEnd
      const currentValue = this.commentContent || ''
      
      // 在光标位置插入表情
      const newValue = currentValue.substring(0, startPos) + emoji + currentValue.substring(endPos)
      this.commentContent = newValue
      
      // 设置新的光标位置（表情后面）
      this.$nextTick(() => {
        const newPos = startPos + emoji.length
        textareaEl.setSelectionRange(newPos, newPos)
        textareaEl.focus()
      })
    },

    // 取输入框内部真正的 textarea 元素（同 handleChooseEmoji）
    getTextareaEl () {
      const textarea = this.$refs.textareaRef
      if (!textarea) return null
      return textarea.$el ? textarea.$el.querySelector('textarea') : textarea
    },

    // 以下三个方法负责让 @ 高亮镜像跟着 textarea 的内部滚动走，
    // 实现细节和「scroll 事件不冒泡所以只能挂原生元素」的原因见主评论框
    syncMirrorScroll () {
      const textareaEl = this.getTextareaEl()
      const mirror = this.$refs.inputMirror
      if (textareaEl && mirror) {
        mirror.scrollTop = textareaEl.scrollTop
      }
      // 锚点是相对输入框定位的，内容滚动后要跟着走，否则会和 "@" 脱开
      if (this.atTriggerIndex >= 0) {
        this.updateMentionAnchor()
      }
    },

    bindMirrorScroll () {
      const textareaEl = this.getTextareaEl()
      if (!textareaEl || this._mirrorScrollEl === textareaEl) return
      this.unbindMirrorScroll()
      this._mirrorScrollEl = textareaEl
      textareaEl.addEventListener('scroll', this.syncMirrorScroll, { passive: true })
    },

    unbindMirrorScroll () {
      if (this._mirrorScrollEl) {
        this._mirrorScrollEl.removeEventListener('scroll', this.syncMirrorScroll)
        this._mirrorScrollEl = null
      }
    },

    // 输入时校准已选的 @ 列表，并判断是不是刚打了 "@" 想找人。
    // 两件事的理由见主评论框（components/comment/index.vue）里 onContentInput 的注释
    onContentInput () {
      if (this.selectedAtUsers.length) {
        const kept = syncAtUsers(this.commentContent, this.selectedAtUsers)
        if (kept.length !== this.selectedAtUsers.length) {
          this.selectedAtUsers = kept
        }
      }

      const textareaEl = this.getTextareaEl()
      const query = detectMentionQuery(this.commentContent, textareaEl ? textareaEl.selectionStart : undefined)
      if (!query) {
        // 正文里那段 "@xxx" 没了就没有任何东西可搜，面板一并收起（面板里没有搜索框）
        this.atTriggerIndex = -1
        this.closeMentionPicker()
        return
      }
      if (this.selectedAtUsers.length >= MAX_AT_USER_COUNT) return

      this.atTriggerIndex = query.index
      if (this.$refs.mentionPicker) {
        this.$refs.mentionPicker.open(query.keyword)
      }
      this.$nextTick(this.updateMentionAnchor)
    },

    // 把选人面板的锚点挪到 "@" 的像素位置上，面板紧跟在 "@" 后面展开。
    // 原理和取舍见主评论框（components/comment/index.vue）里的同名方法
    updateMentionAnchor () {
      const mark = this.$refs.caretProbeMark
      const textareaEl = this.getTextareaEl()
      if (!mark || !textareaEl) return

      const lineHeight = mark.offsetHeight || 20
      const left = mark.offsetLeft
      const top = mark.offsetTop + lineHeight - textareaEl.scrollTop

      this.mentionAnchorStyle = { left: `${left}px`, top: `${top}px` }
      this.$nextTick(() => {
        if (this.$refs.mentionPicker) this.$refs.mentionPicker.reposition()
      })
    },

    closeMentionPicker () {
      if (this.$refs.mentionPicker) {
        this.$refs.mentionPicker.close()
      }
    },

    // 点 @ 按钮：往光标处插一个 "@"，剩下的交给 onContentInput 统一处理。
    // 完整理由见主评论框（components/comment/index.vue）里的同名方法
    onMentionButtonClick () {
      if (this.selectedAtUsers.length >= MAX_AT_USER_COUNT) {
        this.$toast.info(`最多只能@${MAX_AT_USER_COUNT}个人`)
        return
      }

      const textareaEl = this.getTextareaEl()
      const content = this.commentContent || ''
      const start = textareaEl ? textareaEl.selectionStart : content.length
      const end = textareaEl ? textareaEl.selectionEnd : content.length

      // 正文里已经有一段正在输入的 @ 了：只把面板顶出来，别插第二个
      const pending = detectMentionQuery(content, start)
      if (pending) {
        this.atTriggerIndex = pending.index
        if (this.$refs.mentionPicker) this.$refs.mentionPicker.open(pending.keyword)
        if (textareaEl) textareaEl.focus()
        this.$nextTick(this.updateMentionAnchor)
        return
      }

      if (content.length + 1 > COMMENT_MAX_LENGTH) {
        this.$toast.info(`回复最多输入 ${COMMENT_MAX_LENGTH} 个字`)
        return
      }

      const before = content.slice(0, start)
      const after = content.slice(end)
      const prefix = (before && !/\s$/.test(before)) ? ' ' : ''
      const cursor = before.length + prefix.length + 1

      this.commentContent = before + prefix + '@' + after
      this.atTriggerIndex = cursor - 1

      this.$nextTick(() => {
        if (textareaEl) {
          textareaEl.setSelectionRange(cursor, cursor)
          textareaEl.focus()
        }
        if (this.$refs.mentionPicker) this.$refs.mentionPicker.open('')
        this.updateMentionAnchor()
      })
    },

    // 退格删 "@昵称"：第一次按整段选中，再按一次才真的删掉。
    // 为什么做成两步、以及为什么网页端能在 keydown 里拦下来，
    // 见主评论框（components/comment/index.vue）里同名方法的注释
    onTextareaKeydown (e) {
      if (!e || e.key !== 'Backspace') return
      if (e.isComposing) return
      if (e.ctrlKey || e.metaKey || e.altKey) return
      if (!this.selectedAtUsers.length) return

      const textareaEl = this.getTextareaEl()
      if (!textareaEl) return

      const start = textareaEl.selectionStart
      const end = textareaEl.selectionEnd
      if (typeof start !== 'number' || typeof end !== 'number') return
      // 已经有选区 → 不拦，让浏览器原生删掉整段
      if (start !== end) return
      if (start <= 0) return

      const token = findMentionTokenAt(this.commentContent, this.selectedAtUsers, start - 1)
      if (!token) return

      e.preventDefault()
      textareaEl.setSelectionRange(token.start, token.tokenEnd)
    },

    // MentionPicker 选中一个人。两条插入路径的区别见主评论框
    // （components/comment/index.vue）里 handleSelectAtUser 上的注释
    handleSelectAtUser (atUser) {
      if (!atUser || !atUser.userId || !atUser.nickname) return
      if (this.selectedAtUsers.length >= MAX_AT_USER_COUNT) {
        this.$toast.info(`最多只能@${MAX_AT_USER_COUNT}个人`)
        return
      }

      const textareaEl = this.getTextareaEl()
      let result
      if (this.atTriggerIndex >= 0) {
        result = { value: insertMention(this.commentContent, atUser.nickname, this.atTriggerIndex), cursor: -1 }
      } else {
        result = insertMentionAt(
          this.commentContent,
          atUser.nickname,
          textareaEl ? textareaEl.selectionStart : undefined,
          textareaEl ? textareaEl.selectionEnd : undefined
        )
      }

      // el-input 的 maxlength 只约束键盘输入，JS 赋值不受它限制
      if (result.value.length > COMMENT_MAX_LENGTH) {
        this.$toast.info(`回复最多输入 ${COMMENT_MAX_LENGTH} 个字`)
        return
      }

      this.commentContent = result.value
      this.selectedAtUsers = [...this.selectedAtUsers, atUser]
      this.atTriggerIndex = -1

      if (textareaEl) {
        this.$nextTick(() => {
          if (result.cursor >= 0) {
            textareaEl.setSelectionRange(result.cursor, result.cursor)
          }
          textareaEl.focus()
        })
      }
    },

    handleCancle() {
      this.showBox = false
      this.commentContent = ''
      // 取消回复也要清掉已选的 @，否则下次打开回复框会把上次的人带上去
      this.selectedAtUsers = []
      // 同理清掉已选配图：回复框是每条评论一个常驻实例（v-show 控制显隐），
      // 不清的话下次点「回复」会看到上次选的图还挂在那里
      this.resetCommentImages()
      // 通知父组件重新显示主评论输入框
      this.$emit('closeReply')
    },

    async addComment() {
      if (!this.user) {
        this.$store.commit("systemStore/setLoginFlag", true)
        return
      }
      if (!isAuditPublished(this.articleInfo)) {
        const badge = getAuditBadge(this.articleInfo)
        this.$toast.info(badge ? badge.tip : '该帖子当前不可评论')
        return
      }
      // 「只发图不发字」是合法的，判据与后端 insertComment 的判空口径一致
      const hasText = !!(this.commentContent && this.commentContent.trim())
      if (!hasText && !this.hasCommentImages && !this.commentSticker) {
        this.$toast.error('评论不能为空')
        return
      }
      if (this.submitting) return
      this.submitting = true

      // 先传图再发评论：反过来的话传图失败就留下一条本该带图却没有图的回复，
      // 而回复已经发出去了，没法回滚
      let imageUrls = []
      try {
        imageUrls = await this.uploadCommentImagesToCos()
      } catch (err) {
        console.error('回复图片上传失败:', err)
        this.$toast.error(err?.message || '图片上传失败，请重试')
        this.submitting = false
        return
      }

      const browser = browserMatch() || { browser: '', version: '' }
      const content = this.commentContent.trim()
      const comment = {
        articleId: this.articleInfo?.id,
        content,
        replyUserId: this.replyUserId,
        parentId: this.parentId,
        browser: String(browser.browser || '').toLowerCase(),
        browserVersion: `${browser.browser || ''} ${browser.version || ''}`.trim(),
        type: this.articleInfo?.type,
        level: this.level + 1,
        // 添加选择的昵称信息
        nickType: this.selectedNickname ? this.selectedNickname.nickType : 0,
        userId: this.selectedNickname ? this.selectedNickname.id : this.user.id,
        // 这条回复挂在哪所学校名下（落到 b_comment.author_school_id 快照）。
        // 只有普通帖需要传：同校帖服务端会强制用本帖所属学校；不传则回退到
        // 资料展示的那段经历，也就是最近学校。服务端还会校验账号族归属。
        // 选了「不显示学校」时不传学校、改传 hideAuthorSchool=1，口径同 comment/index.vue
        authorSchoolId: (this.selectedNickname && this.selectedNickname.hideSchool)
          ? null : (this.commentSchoolId || null),
        hideAuthorSchool: (this.selectedNickname && this.selectedNickname.hideSchool) ? 1 : 0,
        // 被@的用户id，逗号分隔。以正文为准校准一遍，避免给"名字已经被删掉"的人发通知
        atUserIds: buildAtUserIds(syncAtUsers(content, this.selectedAtUsers)),
        // 配图地址数组。字段名与后端 Comment.imageList 一致
        imageList: imageUrls.length ? imageUrls : null,
        stickerUrl: this.commentSticker ? this.commentSticker.url : null
      }

      userFetch.postComment(comment).then(() => {
        // 审核状态（isPublish / reviewStatus）刻意不做分支：
        // 产品要求回复侧对审核完全无感，待审(2)和不通过(3)都走这条普通成功路径。
        // 未通过审核的回复重新拉列表时不会出现（列表接口带 is_publish = 1）
        this.commentContent = ''
        this.selectedAtUsers = []
        this.resetCommentImages()
        this.commentSticker = null
        this.showBox = false
        this.$emit('closeReply')

        this.$emit("reloadReply", this.parentId)
        this.$toast.success('评论成功')
        this.$store.commit("commonStore/setCommentFlag", true)
      }).catch(err => {
        if (err?.message !== '__SILENT_AUTH__' && err?.message !== '__ACCOUNT_BANNED__') {
          this.$toast.error(err?.message || '回复失败，请稍后重试')
        }
      }).finally(() => {
        this.submitting = false
      })
    }
  }
}
</script>

<style lang="scss" scoped>
// 回复框 textarea 与盖在它上面那层 @ 高亮镜像的共享盒模型。
// 两层必须逐字符对齐，任何一项只改一边都会错开成双影，所以一律走 mixin。
// 完整理由见主评论框 components/comment/index.vue 里的同名 mixin
@mixin comment-input-box {
  box-sizing: border-box;
  width: 100%;
  padding: 12px 15px;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.6;
  letter-spacing: normal;
  text-align: left;
  white-space: pre-wrap;
  overflow-wrap: break-word;
  word-break: normal;
}

.comment-main {
  margin-left: 40px;

  .comment-item {
    border: none;
    border-radius: 8px;
    padding: 0;
    margin: 10px 0 10px;
    background-color: #f7f8fa;
    position: relative;

    .box {
      display: flex;
      padding: 10px;
      padding: 10px 10px 10px 15px;

      .avatar-wrapper {
        position: relative;

        .avatar {
          line-height: normal;
          position: relative;
          vertical-align: middle;
          cursor: pointer;

          img {
            width: 40px;
            height: 40px;
            overflow: hidden;
            border-radius: 50%;
            transition: all 0.3s;
          }

          &:hover img {
            transform: scale(1.05);
          }

          .avatar-badge {
            position: absolute;
            bottom: -2px;
            right: -2px;
            width: 16px;
            height: 16px;
            background: var(--theme-color);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            border: 2px solid #f7f8fa;

            i {
              font-size: 10px;
              color: #fff;
            }
          }
        }
      }

      .ml-3 {
        margin-left: 12px !important;
        width: 100%;
        position: relative;

        .comment-input {
          position: relative;
          padding-right: 15px;

          /* 镜像层的定位父级。不能直接用 .comment-input：它有 padding-right: 15px，
             镜像铺满它会比 textarea 宽出 15px，折行位置全错。
             用 flex 消掉行盒 strut 对容器高度的影响，理由见主评论框 */
          .comment-textarea-wrap {
            position: relative;
            display: flex;
            flex-direction: column;
          }

          /* 光标探针：和 textarea 同款盒模型，只用来量 "@" 的像素位置。
             visibility: hidden 而不是 display: none —— 后者不产生布局，量不到坐标。
             详见主评论框同一处的注释 */
          .comment-caret-probe {
            @include comment-input-box;

            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            overflow: hidden;
            visibility: hidden;
            pointer-events: none;
            z-index: -1;
          }

          .comment-caret-mark {
            display: inline-block;
            width: 0;
            height: 1em;
          }

          /* 选人面板的定位锚点。这个类落在 el-popover 的根节点上（默认是参与文档流的
             inline span，不脱流会在输入框下面撑出一行空白），绝对定位 + 尺寸归零，
             只当定位参照物；具体 left / top 由 updateMentionAnchor 写内联 style。
             详见主评论框同一处的注释 */
          .mention-anchor {
            position: absolute;
            left: 0;
            top: 0;
            width: 0;
            height: 0;
          }

          /* 输入框里的 @ 高亮层，盖在 textarea 上面。细节见主评论框 */
          .comment-input-mirror {
            @include comment-input-box;

            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            /* 盖在上面必须放掉指针事件，否则点击和拖选都落不到输入框上 */
            pointer-events: none;
            z-index: 1;
            /* 溢出交给 syncMirrorScroll 用 scrollTop 对齐，
               不能是 auto/scroll —— 多一条滚动条就会占掉内容宽度 */
            overflow: hidden;
            color: var(--article-color);
            background: transparent;
            user-select: none;

            /* 和评论正文 .at-text 同色。这里不能加 margin，一加光标就偏 */
            .mirror-at-text {
              color: #3d7eff;
            }
          }

          .comment-textarea {
            ::v-deep( .el-textarea__inner) {
              @include comment-input-box;

              color: var(--article-color) !important;
              border-radius: 4px;
              background-color: transparent;
              border: none;
              transition: all 0.25s ease-in-out 0s;
              resize: none;

              &:focus {
                background-color: #fff;
                border-color: #dcdfe6;
              }
            }

            ::v-deep( .el-input__count) {
              background: transparent;
              /* 镜像层是 z-index: 1，字数统计要压在它上面 */
              z-index: 2;
            }

            /* 高亮生效时真实文字让位给镜像层，textarea 只留光标和选区 */
            &.is-highlighting {
              ::v-deep( .el-textarea__inner) {
                color: transparent !important;
                caret-color: var(--article-color);
                /* 滚动条会占掉内容宽度让两层折行位置错开，藏掉它；
                   内容照样能滚，镜像由 syncMirrorScroll 跟着走 */
                scrollbar-width: none;

                &::-webkit-scrollbar {
                  width: 0;
                  height: 0;
                }
              }
            }
          }
        }

        /* 已选表情的预览（输入框下方、工具栏上方）。
           与主评论框（components/comment/index.vue）同一套视觉，逻辑共用 commentImageMixin */
        .comment-sticker-preview {
          position: relative;
          width: 72px;
          height: 72px;
          margin-top: 10px;

          img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            border-radius: 6px;
            background: #f7f7f7;
            display: block;
          }

          /* 这条原来漏了，× 会当成普通流内元素掉到图片下面去，
             而不是贴在右上角 —— 和下面 .comment-upload-item 的删除按钮保持一致 */
          .comment-upload-remove {
            position: absolute;
            top: -6px;
            right: -6px;
            width: 18px;
            height: 18px;
            border-radius: 50%;
            background: rgba(0, 0, 0, 0.6);
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            font-size: 12px;

            &:hover {
              background: rgba(0, 0, 0, 0.8);
            }
          }
        }
        .comment-upload-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 10px;

          .comment-upload-item {
            position: relative;
            width: 72px;
            height: 72px;
            border-radius: 6px;
            /* 删除按钮压在右上角外沿，所以这里不能 overflow: hidden，
               圆角由里面的 img 自己带 */
            flex-shrink: 0;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
              border-radius: 6px;
              display: block;
              background: #f2f3f5;
            }

            .comment-upload-remove {
              position: absolute;
              top: -6px;
              right: -6px;
              width: 18px;
              height: 18px;
              border-radius: 50%;
              background: rgba(0, 0, 0, 0.6);
              color: #fff;
              display: flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              font-size: 12px;

              &:hover {
                background: rgba(0, 0, 0, 0.8);
              }
            }
          }
        }

        .comment-btn {
          display: flex;
          align-items: center;
          margin: 10px 0;

          .emoji-btn {
            i {
              font-size: 1.3rem;
            }
          }

          .sticker-entry-btn {
            margin-left: 12px;
          }

          .sticker-pack-btn {
            margin-left: 10px;
            img {
              width: 22px;
              height: 22px;
              object-fit: contain;
              display: block;
            }
          }

          .emoji-btn-active {
            .iconfont {
              color: var(--theme-color);
            }
          }

          /* @ 按钮：iconfont 里没有 @ 图标，直接用字符，字号与 .emoji-btn 对齐 */
          .mention-btn {
            margin-left: 12px;
            font-size: 1.3rem;
            font-weight: 600;
            line-height: 1;
            color: #606266;
            user-select: none;

            &:hover {
              color: #3d7eff;
            }
          }

          /* 配图按钮。el-upload 默认 inline-block 且带自己的行高，
             不归零会让这一排按钮不等高 */
          .comment-image-upload {
            margin-left: 12px;
            line-height: 1;
          }

          .image-btn {
            font-size: 1.3rem;
            line-height: 1;
            color: #606266;

            &:hover {
              color: #3d7eff;
            }
          }

          .v-comment-btn {
            border: 1px solid #dcdfe6;
            border-radius: 4px;
            text-align: center;
            padding: 10px 16px;
            font-size: 14px;
            transition: all 0.3s;
            outline: none;
            background-color: #ffffff;
            color: #606266;
            
            &:hover {
              background-color: #f5f7fa;
              border-color: #c0c4cc;
            }
            
            &:active {
              background-color: #e8eaed;
            }
            
            &.has-content {
              border-color: #ff6b81;
              background-color: #ff6b81;
              color: #ffffff;
              
              &:hover {
                background-color: #ff8fa3;
                border-color: #ff8fa3;
              }
              
              &:active {
                background-color: #ff4757;
                border-color: #ff4757;
              }
            }
          }

          .cancle-btn {
            margin-right: 1rem;
            position: relative;
            background-color: #ffffff !important;
            border-color: #dcdfe6 !important;
            color: #606266 !important;
            
            &:hover {
              background-color: #f5f7fa !important;
              border-color: #c0c4cc !important;
              color: #606266 !important;
            }
            
            &:active {
              background-color: #e8eaed !important;
            }
          }

          @media screen and (max-width: 767px) {
            .upload-btn {
              margin-left: auto;
              position: relative;
            }
          }

          @media screen and (min-width: 768px) {
            .upload-btn {
              margin-left: auto;
              overflow: hidden;
              transition: all 0.3s;
              position: relative;
            }
          }
        }

        .emoji-wrapper {
          position: absolute;
          top: -53px;
        }
      }
    }
  }
}

// 昵称选择器样式（非 scoped，因为 el-popover 挂载在 body 下）
.nickname-popover {
  padding: 8px 0 !important;
  
  .nickname-list {
    max-height: 300px;
    overflow-y: auto;

    .nickname-item {
      display: flex;
      align-items: center;
      padding: 10px 12px;
      cursor: pointer;
      transition: all 0.3s;
      border-radius: 4px;
      margin: 0 4px;

      &:hover {
        background-color: #f5f7fa;
      }

      &.active {
        background-color: #ecf5ff;
        
        .nickname-name {
          color: var(--theme-color);
          font-weight: 600;
        }
      }

      .nickname-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        margin-right: 12px;
        object-fit: cover;
      }

      .nickname-info {
        flex: 1;
        min-width: 0;

        .nickname-name {
          font-size: 14px;
          color: #303133;
          margin-bottom: 4px;
          font-weight: 500;
        }

        .nickname-type {
          font-size: 12px;
          color: #909399;

          &.is-real {
            color: var(--theme-color);
          }

          &.is-anonymous {
            color: #909399;
          }
        }
      }

      .el-icon-check {
        color: var(--theme-color);
        font-size: 16px;
        font-weight: bold;
        margin-left: 8px;
      }
    }
  }
}
</style>